import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Menu from "@material-ui/core/Menu";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import VariablePicker from "./VariablePicker";
import TokenTextArea from "./TokenTextArea";

import api from "../../services/api";

// Formulário dos campos de um registro do Attio, no lugar do JSON cru.
//
// Por que existe: o JSON escondia o TIPO do atributo, e o tipo é justamente o
// que o Attio valida. Telefone exige E.164, empresa é referência a outro
// registro e não texto, nome em string vira só o primeiro nome. Quem preenche
// JSON descobre isso por 400.
//
// O componente NÃO muda o contrato do nó: ele lê e escreve a MESMA string JSON
// que o campo `body` sempre guardou ({"data":{"values":{…}}}). Assim o build()
// continua `d => d.body`, nada de lógica nova precisa ser espelhada no backend,
// e nó antigo escrito à mão continua abrindo.

const useStyles = makeStyles(theme => ({
  wrap: { marginBottom: 14 },
  cabecalho: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 },
  titulo: { fontSize: 12, fontWeight: 600, color: theme.palette.text.secondary },
  linha: { display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 8 },
  campo: { flex: 1 },
  dupla: { display: "flex", gap: 6, flex: 1 },
  aviso: { fontSize: 11, color: theme.palette.text.secondary, marginTop: 4, lineHeight: 1.45 },
  erro: { fontSize: 11, color: "#B26A00", marginTop: 4, lineHeight: 1.45 },
  acoes: { display: "flex", alignItems: "center", gap: 8, marginTop: 4 },
  itens: { display: "flex", flexDirection: "column", gap: 6, flex: 1 },
  maisItem: { alignSelf: "flex-start" },
}));


// Os 4 que quase todo fluxo usa. O resto entra pelo "+ Adicionar campo".
const PADRAO = ["name", "email_addresses", "phone_numbers", "company", "domains", "title", "content"];

const vazioPara = attr => (attr.multiselect ? [] : "");

// ─────────── leitura/escrita do JSON do nó ───────────

// item de lista guarda em data.entry_values, registro em data.values
const lerValores = (texto, chave) => {
  if (!texto || !String(texto).trim()) return {};
  try {
    const j = JSON.parse(texto);
    return (j && j.data && j.data[chave]) || {};
  } catch {
    return null; // JSON inválido → o chamador cai no modo cru
  }
};

const escreverValores = (textoAtual, valores, chave) => {
  let base = { data: { values: {} } };
  try {
    const j = JSON.parse(textoAtual || "{}");
    if (j && typeof j === "object") base = j;
  } catch {
    /* corpo ilegível: recomeça do envelope padrão */
  }
  if (!base.data || typeof base.data !== "object") base.data = {};
  base.data[chave] = valores;
  return JSON.stringify(base, null, 2);
};

// ─────────── um campo, pelo tipo do atributo ───────────

const CampoDoTipo = ({ attr, valor, onChange, readOnly, opcoes, carregandoOpcoes, pedirOpcoes, abrirPicker, varGroups }) => {
  const classes = useStyles();
  const multi = !!attr.multiselect;

  // as opções de select/status vêm de endpoint próprio: busca ao montar, nunca
  // no corpo do render (setState durante o render de outro componente).
  const precisaOpcoes = attr.type === "select" || attr.type === "status";
  useEffect(() => {
    if (precisaOpcoes && !opcoes && !carregandoOpcoes) pedirOpcoes();
  }, [precisaOpcoes, opcoes, carregandoOpcoes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Um campo de LISTA (e-mails, telefones, domínios) vira uma linha por item, e
  // não texto separado por vírgula: token e vírgula não convivem — \{\{a\}\},\{\{b\}\}
  // seria partido no meio por qualquer caminho que contenha vírgula.
  const itens = multi
    ? Array.isArray(valor)
      ? valor
      : valor === undefined || valor === null || valor === ""
      ? []
      : [valor]
    : [valor];

  const gravar = lista => onChange(multi ? lista.filter(v => v !== undefined) : lista[0]);
  const setItem = (i, v) => {
    const arr = itens.slice();
    arr[i] = v;
    gravar(arr);
  };
  const removerItem = i => gravar(itens.filter((_, k) => k !== i));
  const addItem = () => gravar([...itens, ""]);


  // UM item. Usa o MESMO componente dos campos de texto do drawer: texto livre,
  // chip, ou os dois misturados — o chip é um bloco atômico no meio do texto e
  // o valor guardado continua a string com {{…}}.
  const campoItem = (v, i, { label, extra = {}, ajuda = "" } = {}) => (
    <TokenTextArea
      key={i}
      label={label}
      value={v === undefined || v === null ? "" : String(v)}
      onChange={nv => setItem(i, nv)}
      groups={varGroups}
      placeholder={extra.placeholder}
      helperText={ajuda}
      readOnly={readOnly}
      singleLine
      onOpenPicker={abrirPicker ? insertFn => abrirPicker(insertFn) : undefined}
    />
  );

  // o campo inteiro: um item (simples) ou vários com remover/adicionar
  const simples = (extra = {}, ajuda = "") => {
    if (!multi) return campoItem(itens[0], 0, { label: attr.title, extra, ajuda });
    return (
      <div className={classes.itens}>
        {itens.map((v, i) => (
          <div className={classes.linha} key={i}>
            {campoItem(v, i, { label: i === 0 ? attr.title : "", extra, ajuda: i === itens.length - 1 ? ajuda : "" })}
            {itens.length > 1 && (
              <IconButton size="small" onClick={() => removerItem(i)} disabled={readOnly} title="remover">
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </div>
        ))}
        <Button className={classes.maisItem} size="small" startIcon={<AddIcon />} onClick={addItem} disabled={readOnly}>
          {itens.length ? "mais um" : `adicionar ${attr.title.toLowerCase()}`}
        </Button>
      </div>
    );
  };

  switch (attr.type) {
    case "personal-name": {
      // string sem vírgula vira SÓ o primeiro nome no Attio; o objeto evita isso
      const obj = valor && typeof valor === "object" && !Array.isArray(valor) ? valor : {};
      const upd = p => {
        const next = { ...obj, ...p };
        next.full_name = `${next.first_name || ""} ${next.last_name || ""}`.trim();
        onChange(next);
      };
      const metade = (sub, rotulo) => (
        <TokenTextArea
          label={rotulo}
          value={obj[sub] || ""}
          onChange={nv => upd({ [sub]: nv })}
          groups={varGroups}
          readOnly={readOnly}
          singleLine
          onOpenPicker={abrirPicker ? insertFn => abrirPicker(insertFn) : undefined}
        />
      );
      return (
        <div className={classes.campo}>
          <div className={classes.dupla}>
            {metade("first_name", "Nome")}
            {metade("last_name", "Sobrenome")}
          </div>
          <div className={classes.aviso}>Aceita variável em qualquer um dos dois.</div>
        </div>
      );
    }

    case "phone-number": {
      const semDDI = itens
        .map(v => String(v ?? "").trim())
        .filter(v => v && !v.startsWith("+") && !v.includes("{{"));
      return (
        <div className={classes.campo}>
          {simples({ placeholder: "+5549999992822" })}
          {semDDI.length > 0 && (
            <div className={classes.erro}>
              O Attio exige E.164: comece com <b>+</b> e o código do país. {semDDI.join(", ")} vai ser recusado.
            </div>
          )}
        </div>
      );
    }

    case "select":
    case "status": {
      const atual = Array.isArray(valor) ? valor : valor ? [valor] : [];
      // valor vindo do fluxo não está na lista de opções: cai em texto livre
      const comVariavel = atual.some(v => String(v).includes("{{"));
      if (comVariavel) return simples({}, "Valor vindo do fluxo. Tem de resolver no título exato da opção.");
      return (
        <TextField
          className={classes.campo}
          select={!!opcoes}
          label={attr.title}
          value={multi ? atual : atual[0] || ""}
          onChange={e => {
            const v = e.target.value;
            onChange(multi ? (Array.isArray(v) ? v : [v]) : v);
          }}
          SelectProps={multi ? { multiple: true } : undefined}
          fullWidth variant="outlined" size="small" disabled={readOnly}
          helperText={carregandoOpcoes ? "carregando opções…" : "Vale o título da opção, como está no Attio."}
        >
          {(opcoes || []).map(o => (
            <MenuItem key={o.id || o.title} value={o.title}>
              {o.title}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    case "checkbox":
      return (
        <FormControlLabel
          className={classes.campo}
          control={
            <Switch
              checked={valor === true || valor === "true"}
              onChange={e => onChange(e.target.checked)}
              disabled={readOnly}
              color="primary"
            />
          }
          label={attr.title}
        />
      );

    case "date":
      return simples({ type: String(itens[0] ?? "").includes("{{") ? "text" : "date", InputLabelProps: { shrink: true } },
        "Data ISO (AAAA-MM-DD). Aceita variável.");

    case "number":
    case "rating":
      return simples({}, "Número. Aceita variável.");

    case "currency":
      return simples({}, `Valor numérico${attr.currency ? ` em ${attr.currency}` : ""}. Sem símbolo nem separador de milhar.`);

    case "record-reference":
      return simples(
        { placeholder: "uuid do registro" },
        `Referência a ${attr.relation && attr.relation.object ? attr.relation.object : "outro registro"}. ` +
          "Use o ID do registro. Texto solto é lido como domínio/e-mail, não como nome."
      );

    case "email-address":
      return simples({ placeholder: "pessoa@empresa.com" }, multi ? "Vários: separe por vírgula." : "");

    case "domain":
      return simples({ placeholder: "empresa.com" }, multi ? "Vários: separe por vírgula." : "");

    case "location":
      return simples({ multiline: true, minRows: 2 },
        "Endereço composto: escreva o JSON do objeto de localização do Attio.");

    default:
      return simples({}, multi ? "Vários: separe por vírgula." : "");
  }
};

// ─────────── o campo do drawer ───────────

const AttioValues = ({ field, value, onChange, nodeData, readOnly, scope, varGroups, varRunAt, varsEnabled }) => {
  const classes = useStyles();
  const target = field.target || "objects";
  const identifier = String((nodeData && nodeData[field.identifierField || "object"]) || "").trim();
  const connectorId = nodeData && nodeData.connectorId;
  const chave = field.valuesKey || "values";

  const [attrs, setAttrs] = useState(null);
  const [erro, setErro] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [menu, setMenu] = useState(null);
  const [opcoes, setOpcoes] = useState({});
  const [carregandoOpcoes, setCarregandoOpcoes] = useState({});
  const [cru, setCru] = useState(false);
  const [varAlvo, setVarAlvo] = useState(null); // { attr, sub } — qual campo recebe o dado

  const valores = lerValores(value, chave);
  const ilegivel = valores === null;

  useEffect(() => {
    if (!connectorId || !identifier) {
      setAttrs(null);
      setErro(!connectorId ? "Escolha o conector Attio." : "Informe o objeto para ver os campos.");
      return;
    }
    let vivo = true;
    setCarregando(true);
    setErro("");
    api
      .get("/connectors/attio/attributes", { params: { connectorId, target, identifier } })
      .then(({ data }) => {
        if (!vivo) return;
        setAttrs(data.attributes || []);
        setCarregando(false);
      })
      .catch(e => {
        if (!vivo) return;
        const msg = (e && e.response && e.response.data && e.response.data.error) || "não foi possível ler os campos do Attio";
        setErro(String(msg));
        setAttrs(null);
        setCarregando(false);
      });
    return () => {
      vivo = false;
    };
  }, [connectorId, target, identifier]);

  const pedirOpcoes = useCallback(
    attr => {
      if (opcoes[attr.slug] || carregandoOpcoes[attr.slug]) return;
      setCarregandoOpcoes(p => ({ ...p, [attr.slug]: true }));
      api
        .get("/connectors/attio/options", {
          params: { connectorId, target, identifier, attribute: attr.slug, kind: attr.type },
        })
        .then(({ data }) => setOpcoes(p => ({ ...p, [attr.slug]: data.options || [] })))
        .catch(() => setOpcoes(p => ({ ...p, [attr.slug]: [] })))
        .finally(() => setCarregandoOpcoes(p => ({ ...p, [attr.slug]: false })));
    },
    [connectorId, target, identifier, opcoes, carregandoOpcoes]
  );

  const setValor = (slug, v) => {
    const next = { ...(valores || {}) };
    next[slug] = v;
    onChange(escreverValores(value, next, chave));
  };
  const remover = slug => {
    const next = { ...(valores || {}) };
    delete next[slug];
    onChange(escreverValores(value, next, chave));
  };
  const adicionar = attr => {
    setMenu(null);
    const next = { ...(valores || {}) };
    next[attr.slug] = vazioPara(attr);
    onChange(escreverValores(value, next, chave));
  };

  // insere o dado do fluxo no campo que pediu — e não no JSON inteiro, que é o
  // que faria um picker ligado ao `body`.
  // o campo que abriu o picker devolveu a própria função de inserção: o token
  // entra NO CURSOR, no meio do texto, como nos demais campos do drawer.
  const aplicarVariavel = path => {
    const escolha = varAlvo;
    setVarAlvo(null);
    if (escolha && escolha.insertFn) escolha.insertFn(path);
  };

  // modo cru: JSON ilegível, descoberta indisponível, ou o usuário pediu
  if (cru || ilegivel || (!carregando && !attrs)) {
    return (
      <div className={classes.wrap}>
        <TextField
          label={field.label || "Dados (JSON)"}
          value={value || ""}
          onChange={e => onChange(e.target.value)}
          fullWidth multiline minRows={6} variant="outlined" size="small" disabled={readOnly}
          helperText={field.helperText}
        />
        <div className={ilegivel ? classes.erro : classes.aviso}>
          {ilegivel
            ? "JSON inválido — corrija para voltar ao formulário."
            : erro || "Descoberta de campos indisponível."}
        </div>
        {attrs && (
          <Button size="small" onClick={() => setCru(false)} disabled={readOnly}>
            voltar ao formulário
          </Button>
        )}
      </div>
    );
  }

  if (carregando) {
    return (
      <div className={classes.wrap}>
        <div className={classes.acoes}>
          <CircularProgress size={16} />
          <Typography className={classes.aviso}>lendo os campos de {identifier} no Attio…</Typography>
        </div>
      </div>
    );
  }

  const porSlug = {};
  (attrs || []).forEach(a => {
    porSlug[a.slug] = a;
  });
  const usados = Object.keys(valores || {});
  // nó novo: abre com os 4 principais que existirem neste objeto
  const visiveis = usados.length
    ? usados
    : PADRAO.filter(s => porSlug[s]).slice(0, 4);
  const disponiveis = (attrs || []).filter(a => !visiveis.includes(a.slug));

  return (
    <div className={classes.wrap}>
      <div className={classes.cabecalho}>
        <span className={classes.titulo}>{field.label || "Campos"}</span>
        <Button size="small" onClick={() => setCru(true)} disabled={readOnly}>
          ver JSON
        </Button>
      </div>

      {visiveis.map(slug => {
        const attr = porSlug[slug] || { slug, title: slug, type: "text" };
        return (
          <div className={classes.linha} key={slug}>
            <CampoDoTipo
              attr={attr}
              valor={(valores || {})[slug]}
              onChange={v => setValor(slug, v)}
              readOnly={readOnly}
              opcoes={opcoes[slug]}
              carregandoOpcoes={!!carregandoOpcoes[slug]}
              pedirOpcoes={() => pedirOpcoes(attr)}
              varGroups={varGroups}
              abrirPicker={varsEnabled ? insertFn => setVarAlvo({ insertFn }) : undefined}
            />
            <IconButton size="small" onClick={() => remover(slug)} disabled={readOnly} title="remover campo">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        );
      })}

      <div className={classes.acoes}>
        <Button
          size="small" startIcon={<AddIcon />} disabled={readOnly || !disponiveis.length}
          onClick={e => setMenu(e.currentTarget)}
        >
          Adicionar campo
        </Button>
        <span className={classes.aviso}>
          {disponiveis.length} de {(attrs || []).length} graváveis em {identifier}
        </span>
      </div>
      <VariablePicker
        open={!!varAlvo}
        scope={scope}
        groups={varGroups}
        runAt={varRunAt}
        onClose={() => setVarAlvo(null)}
        onPick={aplicarVariavel}
      />
      <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
        {disponiveis.map(a => (
          <MenuItem key={a.slug} onClick={() => adicionar(a)}>
            {a.title}
            <span style={{ opacity: 0.5, marginLeft: 8, fontSize: 11 }}>{a.type}</span>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default AttioValues;
